import React from 'react';
import * as styles from './technoCardSection.module.scss';
import { usePageContext } from '../context/pageContext';
import { SanityCountrySettings, SanityTechnoCardSection, TechnoPagesQuery } from 'graphql-types';
import { CTA } from '../html-tag/Cta';
import { graphql, useStaticQuery } from 'gatsby';
import { Blocks } from '../ui/blocks/Blocks';
import { LocalLink } from '../html-tag/localLink';
import ArrowRightOrange from '../../../static/svg/arrow-right-orange.svg';
import slugify from 'slugify';
import { findTranslation } from '../../utils/findTranslation';
import { constructSlug } from '../../utils/constructSlug';
import { faCardClub } from '@fortawesome/pro-regular-svg-icons';
import { SanityPageExpertise } from '../../../graphql-types';
import { isCountryAllowed } from '../../utils/isCountryAllowed';
import { useGeoContext } from '../context/useGeoContext';

interface Props {
  section: SanityTechnoCardSection;
}

const images = [
  '/images/WbkFondCard1.png',
  '/images/WbkFondCard2.png',
  '/images/WbkFondCard3.png',
  '/images/WbkFondCard4.png',
];

export const TechnoCardSection: React.FC<Props> = ({ section }) => {
  const { lang, defaultLang } = usePageContext();
  const { country } = useGeoContext();
  const pages = useStaticQuery<TechnoPagesQuery>(TECHNOPAGESLUG);

  return (
    <div className={styles.wrapper}>
      {section?.activeText && (
        <div className={styles.text}>
          <Blocks blocks={findTranslation(section?._rawTechnoText, lang, defaultLang)} />
        </div>
      )}
      <div className={styles.cards}>
        {section?.technoCardsList
          ?.filter((card) =>
            isCountryAllowed(
              country,
              card?.ctaTechnoCard?.countrySettings as SanityCountrySettings,
            ),
          )
          .map((technoCard, index) => {
            const customImageStyles = {
              backgroundImage: `url("${technoCard?.bgImageTechnoCard?.asset?.gatsbyImageData?.images?.fallback?.src}")`,
            };

            const slug = constructSlug({
              destination: technoCard?.ctaTechnoCard as SanityPageExpertise,
            });

            let desktopImageSelected;
            if ((index + 1) % 4 === 0) desktopImageSelected = images[3];
            else if ((index + 1) % 3 === 0) desktopImageSelected = images[2];
            else if ((index + 1) % 2 === 0) desktopImageSelected = images[1];
            else if ((index + 1) % 1 === 0) desktopImageSelected = images[0];

            return (
              <LocalLink
                to={slug ?? ''}
                className={styles.technoCard}
              >
                <div className={styles.bgMobile}>
                  <img
                    src="/images/WbkFondCardMobile.png"
                    alt=""
                  />
                </div>

                <div className={styles.bgDesktop}>
                  <img src={desktopImageSelected} />
                </div>

                <div className={styles.content}>
                  <h2>{findTranslation(technoCard?._rawTitleTechnoCard, lang, defaultLang)}</h2>
                  <Blocks
                    blocks={findTranslation(technoCard?._rawTextTechnoCard, lang, defaultLang)}
                  />
                  <div className={styles.desktopOnly}>
                    <CTA
                      to={slug ?? ''}
                      label={findTranslation(
                        pages?.sanityFunctionButton?._rawLearnMore,
                        lang,
                        defaultLang,
                      )}
                      dataType={'productNoBorder'}
                    />
                  </div>
                </div>
                <div
                  className={styles.image}
                  style={customImageStyles}
                />
                <div className={styles.arrow}>
                  <ArrowRightOrange />
                </div>
              </LocalLink>
            );
          })}
      </div>
      {section?.activeButton && (
        <div className={styles.button}>
          <CTA
            label={findTranslation(section?._rawTechnoButton, lang, defaultLang)}
            dataType={section?.colorNav ?? ''}
            to={'/our-technologies'}
          />
        </div>
      )}
    </div>
  );
};

export const TECHNOPAGESLUG = graphql`
  query TechnoPages {
    sanityFunctionButton {
      _rawLearnMore(resolveReferences: { maxDepth: 10 })
    }
    allSanityPage {
      nodes {
        id
        _rawPageSlug(resolveReferences: { maxDepth: 10 })
        pagesExpertiseList {
          id
          _rawPageExpertiseSlug(resolveReferences: { maxDepth: 10 })
          pagesProductList {
            id
            _rawPageProductSlug(resolveReferences: { maxDepth: 10 })
          }
        }
      }
    }
  }
`;
